import { BackgroundColor } from '@/constants/enum';
import { IPreview } from '@/types/components/preview';
import { BlockStack, Collapsible, InlineStack, Text, TextField } from '@shopify/polaris';
import React, { useCallback, useState } from 'react';
import { ColorPicker, useColor } from 'react-color-palette';
import 'react-color-palette/css';
import { debounce } from 'lodash';
import { ColorPickerCustomerStyled } from './styled';
import { ISetupTermAndCondition } from '@/redux/slice/termAndCondition.slice';
import CustomBadge from '../CustomBadge';

interface ColorPickerCustomProps {
  label: React.ReactNode;
  color: string;
  onChange: (value: string) => void;
  keyColor?: keyof IPreview | keyof ISetupTermAndCondition;
  defaultColor?: BackgroundColor;
  requiredIndicator?: boolean;
  disabled?: boolean;
  showBadge?: boolean;
}

const ColorPickerCustom: React.FC<ColorPickerCustomProps> = ({
  label,
  color,
  keyColor,
  defaultColor = BackgroundColor.Black,
  onChange,
  requiredIndicator,
  disabled = false,
  showBadge = false,
}) => {
  const [isShowColorPicker, setIsShowColorPicker] = useState(false);
  const [selectedColor, setSelectedColor] = useColor(color || '');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceChangeColor = useCallback(
    debounce((color: string) => {
      onChange?.(color);
    }, 300),
    [onChange],
  );

  const handleShowColorPicker = () => {
    if (disabled) return;
    setIsShowColorPicker(!isShowColorPicker);
  };

  return (
    <ColorPickerCustomerStyled bgColorPreview={color} disabled={disabled}>
      <BlockStack gap="200">
        <BlockStack gap="100">
          <Text as="span" variant="bodyMd">
            <span style={{ opacity: disabled ? 0.5 : 1 }}>{label}</span>{' '}
            {requiredIndicator ? <span style={{ color: 'red' }}>*</span> : ''}
            {showBadge && disabled && <CustomBadge />}
          </Text>

          <InlineStack gap="300">
            <div className="preview-color" onClick={handleShowColorPicker}></div>

            <div style={{ flex: 1 }}>
              <TextField
                label
                onChange={(value) => onChange(value)}
                autoComplete=""
                value={color}
                // onBlur={handleOnBlur}
                disabled={disabled}
              />
            </div>
          </InlineStack>
        </BlockStack>

        <Collapsible
          id="collapsible-color-picker"
          open={isShowColorPicker}
          expandOnPrint
          transition={{ duration: '300ms', timingFunction: 'ease-in-out' }}
        >
          <ColorPicker
            // hideAlpha
            hideInput
            height={100}
            onChange={(color) => {
              setSelectedColor(color);
              debounceChangeColor(color.hex);
            }}
            color={selectedColor}
            disabled={disabled}
          />
        </Collapsible>
      </BlockStack>
    </ColorPickerCustomerStyled>
  );
};

export default ColorPickerCustom;
